import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useAuth} from '../store/authContext';
import {useNotify} from '../store/notifyContext';
import admin from '../styles/AdminDashboard.module.css';
import styles from '../styles/StockMockPage.module.css';

const BASE = '/api/staff/stock-mock/';
const EMPTY = {sources: [], recipes: [], reservations: [], packets: []};
const samePool = (a, b) => ['source_id', 'company_id', 'warehouse_id'].every(k => a[k] === b[k]);
const blankComponent = () => ({product_id: '', quantity: '1', unit_code: ''});
const uniqueId = () => Array.from(window.crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('');
const date = value => value ? new Date(value).toLocaleString('ru-RU') : '—';

function Money({value, currency, exVat, status}) {
    if (value == null) {
        const messages = {
            no_positive_quantity: 'Нет положительного остатка в пуле',
            invalid_cost: 'Некорректная себестоимость',
        };
        return <span className={styles.muted}>{messages[status] || 'Не передана'}</span>;
    }
    return <><span className={styles.amount}>{value}</span>
        {currency != null && <small>Валюта: {currency}</small>}
        {exVat != null && <small>Без НДС: {exVat}</small>}
    </>;
}

function readFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Не удалось прочитать файл'));
        reader.readAsText(file, 'UTF-8');
    });
}

function quantity(value) {
    const input = value.trim().replace(',', '.');
    if (!/^\d+(?:\.\d{1,5})?$/.test(input)) throw new Error('Норма: положительное число, до пяти знаков после точки');
    const [whole, fraction = ''] = input.split('.');
    return `${whole.replace(/^0+(?=\d)/, '')}.${fraction.padEnd(5, '0')}`;
}

export default function StockMockPage() {
    const {authFetch} = useAuth();
    const notify = useNotify();
    const [data, setData] = useState(EMPTY);
    const [status, setStatus] = useState('loading');
    const [busy, setBusy] = useState(false);
    const [file, setFile] = useState(null);
    const [receipt, setReceipt] = useState(null);
    const [selection, setSelection] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [name, setName] = useState('');
    const [recipeId, setRecipeId] = useState('');
    const [components, setComponents] = useState([blankComponent()]);
    const [cart, setCart] = useState([]);
    const requestVersion = useRef(0);
    const retry = useRef(null);

    const load = useCallback(async () => {
        const version = ++requestVersion.current;
        try {
            const res = await authFetch(BASE);
            if (version !== requestVersion.current) return;
            if (res.status === 404) {setStatus('disabled'); return;}
            if (!res.ok) throw new Error('Не удалось загрузить состояние обмена');
            const body = await res.json();
            if (version !== requestVersion.current) return;
            setData(body);
            setStatus('ready');
        } catch {
            if (version === requestVersion.current) setStatus('error');
        }
    }, [authFetch]);

    useEffect(() => {
        load();
        const timer = window.setInterval(() => {
            if (!document.hidden) load();
        }, 5000);
        return () => {window.clearInterval(timer); requestVersion.current += 1;};
    }, [load]);

    const pools = useMemo(() => data.sources.flatMap(source => source.scope.map((scope, index) => ({
        ...scope, source_id: source.source_id,
        value: JSON.stringify([source.source_id, scope.company_id, scope.warehouse_id]),
        label: `${source.source_id} · область ${index + 1}`,
    }))), [data.sources]);
    const pool = pools.find(p => p.value === selection) || pools[0];
    const source = data.sources.find(s => s.source_id === pool?.source_id);
    const rows = (source?.items || []).filter(r => samePool({...r, source_id: source.source_id}, pool));
    const filtered = rows.filter(r => `${r.product_name || ''} ${r.product_code || ''} ${r.product_article || ''}`.toLowerCase().includes(search.toLowerCase()));
    const recipes = data.recipes.filter(r => pool && samePool(r, pool));
    const reservations = data.reservations.filter(r => pool && samePool(r, pool));
    const selectedPool = () => ({source_id: pool.source_id, company_id: pool.company_id, warehouse_id: pool.warehouse_id});

    async function send(url, options) {
        const res = await authFetch(url, options);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || body.detail || 'Не удалось выполнить операцию');
        return body;
    }

    async function action(work) {
        if (busy) return;
        setBusy(true);
        try {await work(); await load();}
        catch (error) {notify.error(error.message || 'Соединение потеряно. Повторите запрос');}
        finally {setBusy(false);}
    }

    function upload(event) {
        event.preventDefault();
        action(async () => {
            if (!file) throw new Error('Выберите JSON-пакет');
            if (file.size > 10485760) throw new Error('Файл больше 10 МБ');
            let packet;
            try {packet = JSON.parse(await readFile(file));}
            catch {throw new Error('Файл должен содержать корректный JSON');}
            if (!/^change-[1-9][0-9]*$/.test(packet?.packet_id)) throw new Error('Нужен пакет v2 change-N, не полный снимок tis77-N');
            setReceipt(null);
            const accepted = await send(`${BASE}packets/${packet.packet_id}`, {
                method: 'PUT', headers: {'Content-Type': 'application/json'}, body: file,
            });
            setReceipt(accepted);
        });
    }

    function saveRecipe(event) {
        event.preventDefault();
        action(async () => {
            const recipe = {...selectedPool(), name, recipe_id: recipeId,
                components: components.map(c => ({...c, quantity: quantity(c.quantity)}))};
            await send(`${BASE}recipes/`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(recipe)});
            notify.success('Тестовый состав сохранён');
        });
    }

    function reserve() {
        action(async () => {
            const payload = {...selectedPool(), items: cart};
            const signature = JSON.stringify(payload);
            if (retry.current?.signature !== signature) retry.current = {signature, id: uniqueId()};
            const result = await send(`${BASE}reservations/`, {method: 'POST', headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({...payload, reservation_id: retry.current.id})});
            notify.success(result.status === 'reserved' ? 'Вся тестовая корзина зарезервирована' : 'Этот тестовый резерв уже отменён');
            setCart([]);
            retry.current = null;
        });
    }

    function editComponent(index, fields) {
        setComponents(prev => prev.map((c, i) => i === index ? {...c, ...fields} : c));
    }

    function addToCart(recipe) {
        setCart(prev => {
            const old = prev.find(item => item.recipe_id === recipe.recipe_id);
            return old ? prev.map(item => item === old ? {...item, quantity: item.quantity + 1} : item)
                : [...prev, {recipe_id: recipe.recipe_id, quantity: 1}];
        });
    }

    return <div className={`${admin.page} ${styles.page}`}><div className={admin.shell}>
        <header className={admin.head}>
            <Link to="/admin">Администрирование</Link>
            <h1 className={styles.title}>Обмен с 1С</h1>
            <p>Тестовый файловый стенд · v2 patch_absolute</p>
            <p className={styles.warning}>Реальные товары, заказы и оплаты не изменяются. Пакеты принимаются через API или ручную загрузку JSON.</p>
            <p className={styles.muted}>Фирменный резерв и отражение заказов не передаются контрактом. Наличие здесь — тестовый расчёт, а не гарантия продажи.</p>
        </header>
        <section className={admin.panel}>
            <h2>Приём пакетов</h2>
            <form onSubmit={upload} className={styles.upload}>
                <label>JSON-пакет 1С<input type="file" accept=".json,application/json" disabled={busy} onChange={e => setFile(e.target.files?.[0] || null)}/></label>
                <button disabled={busy || !file || status !== 'ready'}>Принять пакет</button>
                <button type="button" disabled={busy} onClick={load}>Обновить состояние</button>
            </form>
            <p className={styles.muted}>Первый пакет нового источника — bootstrap. Далее принимаются частичные изменения; отсутствующие строки не обнуляются. Автообновление каждые 5 секунд.</p>
            {receipt && <output className={styles.receipt}>Принят пакет {receipt.packet_id}: строк {receipt.accepted_items}, явных нулей {receipt.explicit_zeros}<br/>SHA-256: {receipt.sha256}</output>}
            {status === 'loading' && <p role="status">Загрузка состояния обмена…</p>}
            {status === 'disabled' && <p role="status">Стенд отключён. Установите STOCK_MOCK_ENABLED=true в backend/.env и пересоздайте backend-контейнер.</p>}
            {status === 'error' && <p role="status">Не удалось загрузить стенд. Проверьте доступ сотрудника и соединение с backend.</p>}
            {status === 'ready' && !pools.length && <p>Пакеты ещё не поступали. Загрузите первый пакет с bootstrap: true.</p>}
        </section>
        {pool && <>
            <section className={admin.panel}>
                <div className={styles.toolbar}>
                    <label>Источник и склад<select value={pool.value} disabled={busy} onChange={e => {
                        setSelection(e.target.value); setCart([]); setComponents([blankComponent()]);
                        setRecipeId(''); setName(''); setPage(0); retry.current = null;
                    }}>{pools.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}</select></label>
                    <div>Ревизия: <strong>{source.source_revision}</strong><br/>В 1С: {date(source.data_as_of)}<br/>Принято: {date(source.received_at)}</div>
                </div>
                <details><summary>Идентификаторы и граница учёта</summary>
                    <dl className={styles.details}><dt>Фирма</dt><dd>{pool.company_id}</dd><dt>Склад</dt><dd>{pool.warehouse_id}</dd><dt>ТА</dt><dd>{source.cut?.position}</dd></dl>
                </details>
                <h2>Остатки компонентов</h2>
                <p className={styles.muted}>Цены и средняя себестоимость переданы за базовую единицу товара. Коды валют сохранены как в 1С, без пересчёта. Себестоимость относится к общему пулу фирмы и МОЛ, а не отдельному складу.</p>
                <label>Поиск компонента<input value={search} onChange={e => {setSearch(e.target.value); setPage(0);}} placeholder="Название, код или артикул"/></label>
                <div className={styles.tableWrap}><table aria-label="Остатки компонентов"><thead><tr><th>Компонент</th><th>Код</th><th>Артикул</th><th>Из 1С</th><th>Удержано</th><th>Доступно</th><th>Розничная цена</th><th>Средняя себестоимость</th><th>Проверка</th></tr></thead>
                    <tbody>{filtered.slice(page * 50, page * 50 + 50).map(row => <tr key={row.product_id}>
                        <td>{row.product_name || 'Метаданные ещё не поступали'}<small>{row.unit_name || '—'}</small></td><td>{row.product_code || '—'}</td>
                        <td className={styles.article}>{row.product_article || '—'}</td>
                        <td>{row.quantity}</td><td>{row.held}</td><td>{row.available}</td>
                        <td><Money value={row.retail_price} currency={row.retail_price_currency}/></td>
                        <td><Money value={row.stock_cost} currency={row.stock_cost_currency} exVat={row.stock_cost_ex_vat} status={row.stock_cost_status}/></td>
                        <td>{row.conflict ? <strong className={styles.danger}>Дефицит резерва</strong> : '—'}</td>
                    </tr>)}</tbody></table></div>
                {!filtered.length && <p>Компоненты не найдены.</p>}
                <div className={styles.toolbar}><span>Всего: {filtered.length} · по 50 строк</span>
                    <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>Предыдущая</button>
                    <button disabled={(page + 1) * 50 >= filtered.length} onClick={() => setPage(p => p + 1)}>Следующая</button>
                </div>
            </section>
            <div className={styles.columns}>
                <section className={admin.panel}>
                    <h2>Фиксированный тестовый состав</h2>
                    <p className={styles.muted}>Только компоненты выбранного склада, без замены фасовок. Для отдельной упаковки создайте состав из одного компонента с нормой 1. Повтор ID создаёт новую версию.</p>
                    <form onSubmit={saveRecipe}>
                        <label>ID состава<input required value={recipeId} maxLength={128} onChange={e => setRecipeId(e.target.value)} autoCapitalize="none"/></label>
                        <label>Название состава<input required value={name} onChange={e => setName(e.target.value)}/></label>
                        {components.map((component, index) => <div className={styles.component} key={index}>
                            <label>Компонент {index + 1}<select required value={component.product_id} onChange={e => {
                                const row = rows.find(r => r.product_id === e.target.value);
                                editComponent(index, {product_id: row?.product_id || '', unit_code: row?.unit_code || ''});
                            }}><option value="">Выберите компонент</option>{rows.filter(r => r.unit_code).map(row => <option key={row.product_id} value={row.product_id}>{row.product_code} · {row.product_name}</option>)}</select></label>
                            <label>Норма {index + 1}<input required inputMode="decimal" value={component.quantity} onChange={e => editComponent(index, {quantity: e.target.value})}/></label>
                            <button type="button" disabled={components.length === 1 || busy} onClick={() => setComponents(prev => prev.filter((_, i) => i !== index))}>Убрать</button>
                        </div>)}
                        <div className={styles.toolbar}>
                            <button type="button" disabled={busy || components.length >= 100} onClick={() => setComponents(prev => [...prev, blankComponent()])}>Добавить компонент</button>
                            <button disabled={busy}>Сохранить состав</button>
                        </div>
                    </form>
                </section>
                <section className={admin.panel}>
                    <h2>Комплекты и тестовая корзина</h2>
                    <p className={styles.muted}>Наличие каждого комплекта показано отдельно. Общие детали проверяются вместе при резервировании всей корзины.</p>
                    {!recipes.length && <p>Сохраните первый состав слева.</p>}
                    {recipes.map(recipe => <div className={styles.recipe} key={recipe.recipe_id}>
                        <strong>{recipe.name}</strong><span>Версия {recipe.version} · можно собрать: {recipe.available}</span>
                        <details><summary>Состав</summary>{recipe.components.map(c => <div key={c.product_id}>
                            {rows.find(r => r.product_id === c.product_id)?.product_name || c.product_id} × {c.quantity}
                        </div>)}</details>
                        <div className={styles.toolbar}>
                            <button disabled={busy} onClick={() => {setRecipeId(recipe.recipe_id); setName(recipe.name); setComponents(recipe.components);}}>Изменить состав</button>
                            <button disabled={busy || recipe.available <= 0} aria-label={`В тестовую корзину: ${recipe.name}`} onClick={() => addToCart(recipe)}>В тестовую корзину</button>
                        </div>
                    </div>)}
                    <h3>Тестовая корзина</h3>
                    {!cart.length && <p>Корзина пуста.</p>}
                    {cart.map(item => <div className={styles.toolbar} key={item.recipe_id}>
                        <span>{recipes.find(r => r.recipe_id === item.recipe_id)?.name || item.recipe_id} × {item.quantity}</span>
                        <button disabled={busy} onClick={() => setCart(prev => prev.filter(i => i !== item))}>Удалить</button>
                    </div>)}
                    <button disabled={busy || !cart.length} onClick={reserve}>Зарезервировать всю корзину</button>
                    <h3>Тестовые резервы</h3>
                    {!reservations.length && <p>Резервов пока нет.</p>}
                    {reservations.slice().reverse().map(r => <div key={r.reservation_id} className={styles.recipe}>
                        <code>{r.reservation_id}</code><span>{r.status === 'reserved' ? 'Удерживается' : 'Отменён'}</span>
                        {r.items.map((item, i) => <span key={i}>{item.name} × {item.quantity}</span>)}
                        {r.status === 'reserved' && <button disabled={busy} aria-label={`Отменить тестовый резерв ${r.reservation_id}`} onClick={() => action(async () => {
                            await send(`${BASE}reservations/${encodeURIComponent(r.reservation_id)}/`, {method: 'DELETE'});
                        })}>Отменить тестовый резерв</button>}
                    </div>)}
                </section>
            </div>
        </>}
        <section className={admin.panel}><h2>История принятых пакетов</h2>
            {!data.packets.length && <p>История пуста.</p>}
            <div className={styles.tableWrap}><table><thead><tr><th>Источник</th><th>Пакет</th><th>Строк</th><th>Явных нулей</th><th>Принят</th></tr></thead>
                <tbody>{data.packets.slice().reverse().slice(0, 100).map(p => <tr key={JSON.stringify([p.source_id, p.packet_id])}>
                    <td>{p.source_id}</td><td><details><summary>{p.packet_id}</summary><code>{p.sha256}</code></details></td><td>{p.response.accepted_items}</td><td>{p.response.explicit_zeros}</td><td>{date(p.response.applied_at)}</td>
                </tr>)}</tbody></table></div>
            {data.packets.length > 100 && <p>Показаны последние 100 пакетов. Все квитанции сохраняются на сервере.</p>}
        </section>
    </div></div>;
}
